import React, { useState, Component } from 'react';
import { Button } from '../ui/button';
import { Copy, Share2, Twitter, Facebook, MessageCircle, Link as LinkIcon, Check, Clipboard, ExternalLink } from 'lucide-react';
import { cn } from '../../lib/utils';
import { getPublicTranscriptPageUrl } from '../../lib/api';
interface ShareTranscriptProps {
  taskId: string;
  title?: string;
  className?: string;
  variant?: 'default' | 'compact';
}
export function ShareTranscript({
  taskId,
  title = 'TikTok Transcript',
  className,
  variant = 'default'
}: ShareTranscriptProps) {
  const [copied, setCopied] = useState(false);
  const [showShareOptions, setShowShareOptions] = useState(false);
  const shareUrl = getPublicTranscriptPageUrl(taskId, 'app');
  const encodedTitle = encodeURIComponent(title);
  const encodedUrl = encodeURIComponent(shareUrl);
  const shareText = `Check out this transcript from ${title} via ScribeTok`;
  const encodedShareText = encodeURIComponent(shareText);
  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const toggleShareOptions = () => {
    setShowShareOptions(!showShareOptions);
  };
  // Share to Twitter
  const shareToTwitter = () => {
    window.open(`https://twitter.com/intent/tweet?text=${encodedShareText}&url=${encodedUrl}`, '_blank');
  };
  // Share to Facebook
  const shareToFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`, '_blank');
  };
  // Share via SMS
  const shareViaSms = () => {
    window.open(`sms:?body=${encodedShareText} ${encodedUrl}`, '_blank');
  };
  // Share via WhatsApp
  const shareViaWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodedShareText} ${encodedUrl}`, '_blank');
  };
  if (variant === 'compact') {
    return <div className={cn('relative', className)} data-id="element-1378">
        <Button variant="outline" size="sm" className="gap-2" onClick={toggleShareOptions} data-id="element-1379">
          <Share2 size={16} data-id="element-1380" />
          Share
        </Button>
        {showShareOptions && <div className="absolute top-full right-0 mt-2 p-3 bg-card rounded-lg border border-border shadow-lg z-10 w-64" data-id="element-1381">
            <div className="grid grid-cols-2 gap-2" data-id="element-1382">
              <Button variant="ghost" size="sm" className="justify-start" onClick={handleCopyLink} data-id="element-1383">
                {copied ? <Check size={14} className="mr-2" data-id="element-1384" /> : <Copy size={14} className="mr-2" data-id="element-1385" />}
                Copy Link
              </Button>
              <Button variant="ghost" size="sm" className="justify-start" onClick={shareToTwitter} data-id="element-1386">
                <Twitter size={14} className="mr-2" data-id="element-1387" />
                Twitter
              </Button>
              <Button variant="ghost" size="sm" className="justify-start" onClick={shareToFacebook} data-id="element-1388">
                <Facebook size={14} className="mr-2" data-id="element-1389" />
                Facebook
              </Button>
              <Button variant="ghost" size="sm" className="justify-start" onClick={shareViaSms} data-id="element-1390">
                <MessageCircle size={14} className="mr-2" data-id="element-1391" />
                SMS
              </Button>
            </div>
            <Button variant="ghost" size="sm" className="w-full justify-start mt-2" onClick={shareViaWhatsApp} data-id="element-1392">
              <span className="mr-2" data-id="element-1393">📱</span>
              WhatsApp
            </Button>
          </div>}
      </div>;
  }
  return <div className={cn('bg-secondary/20 rounded-xl p-4 border border-border/40', className)} data-id="element-1394">
      <div className="flex items-center justify-between mb-3" data-id="element-1395">
        <h3 className="font-medium flex items-center" data-id="element-1396">
          <Share2 size={16} className="mr-2 text-primary" data-id="element-1397" />
          Share This Transcript
        </h3>
        <div className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full" data-id="element-1398">
          Share with friends 💬
        </div>
      </div>
      <div className="mb-3" data-id="element-1399">
        <div className="flex" data-id="element-1400">
          <input type="text" value={shareUrl} readOnly className="flex-1 px-3 py-2 rounded-l-lg bg-background border border-r-0 border-border focus:outline-none text-sm" data-id="element-1401" />
          <Button variant="default" className="rounded-l-none" onClick={handleCopyLink} data-id="element-1402">
            {copied ? <Check size={16} data-id="element-1403" /> : <Copy size={16} data-id="element-1404" />}
            <span className="ml-2 hidden sm:inline" data-id="element-1405">
              {copied ? 'Copied!' : 'Copy'}
            </span>
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-id="element-1406">
        <Button variant="outline" size="sm" className="w-full" onClick={shareToTwitter} data-id="element-1407">
          <Twitter size={14} className="mr-2" data-id="element-1408" />
          Twitter
        </Button>
        <Button variant="outline" size="sm" className="w-full" onClick={shareToFacebook} data-id="element-1409">
          <Facebook size={14} className="mr-2" data-id="element-1410" />
          Facebook
        </Button>
        <Button variant="outline" size="sm" className="w-full" onClick={shareViaSms} data-id="element-1411">
          <MessageCircle size={14} className="mr-2" data-id="element-1412" />
          SMS
        </Button>
        <Button variant="outline" size="sm" className="w-full" onClick={shareViaWhatsApp} data-id="element-1413">
          <span className="mr-2" data-id="element-1414">📱</span>
          WhatsApp
        </Button>
      </div>
      <div className="mt-3 text-xs text-muted-foreground flex items-center justify-center" data-id="element-1415">
        <LinkIcon size={12} className="mr-1" data-id="element-1416" />
        <span data-id="element-1417">Help friends save time with this transcript</span>
      </div>
    </div>;
}